(() => {

/**
 * ============================================================
 * xl() — Animated Dropdown / Select Component
 * ============================================================
 * Animates custom dropdown lists using GSAP.
 * Finds containers via [data-select] (or a custom selector),
 * then wires up open/close behaviour and option-selection events.
 *
 * @param {Object} e  Configuration object:
 *   containers      {string|NodeList|Element}  Selector / node(s) to initialise.
 *   showElements    {number}   How many items to show before scrolling (0 = all).
 *   duration        {number}   GSAP animation duration in seconds (default 0.5).
 *   ease            {string}   GSAP easing string (default "power1.out").
 *   delay           {number}   GSAP animation delay in seconds (default 0).
 *   onAnimComplete  {Function} Callback fired when the open/close animation ends.
 *   onOptionSelect  {Function} Callback fired when a list item is clicked.
 *   buttonSelector  {string}   Selector for the trigger button (default "[data-select-name]").
 *   listSelector    {string}   Selector for the dropdown list wrapper (default "[data-select-list]").
 *   optionSelector  {string}   Selector for individual list items (default "[data-select-item]").
 */
var xl = function (e) {
  var t; // Will hold the list of container elements

  if (!e) e = {};

  // ── Resolve container elements ────────────────────────────────────────────
  if (e.containers) {
    if (typeof e.containers === "string") {
      // CSS selector string → query the DOM
      t = document.querySelectorAll(e.containers);
    } else if (e.containers.length && !e.containers.tagName) {
      // Array-like (NodeList, Array) → convert to plain Array
      t = Array.from(e.containers);
    } else if (typeof e.containers === "object" && "tagName" in e.containers) {
      // Single Element → wrap in array
      t = [e.containers];
    }
  } else {
    // Fallback: find every element that has a [data-select] attribute
    t = document.querySelectorAll("[data-select]");
  }

  // Bail out if nothing was found
  if (!t || t.length === 0) return;

  // ── Read top-level config with defaults ───────────────────────────────────
  var showElements  = e.showElements    || 0;           // max visible items before scroll
  var duration      = e.duration        || 0.5;         // animation duration (s)
  var ease          = e.ease            || "power1.out"; // GSAP easing
  var delay         = e.delay           || 0;            // animation start delay (s)
  var onAnimComplete = e.onAnimComplete;                 // optional completion callback
  var onOptionSelect = e.onOptionSelect;                 // required: called on item click

  // ── Initialise each container ─────────────────────────────────────────────
  t.forEach(function (container) {
    initDropdown(container);
  });

  /**
   * Wires up a single dropdown container.
   * @param {Element} container  The root wrapper element.
   */
  function initDropdown(container) {
    // Selectors (can be overridden per-instance via config)
    var buttonSelector = e.buttonSelector || "[data-select-name]";
    var listSelector   = e.listSelector   || "[data-select-list]";
    var optionSelector = e.optionSelector || "[data-select-item]";

    // DOM references inside this container
    var triggerButton  = container.querySelector(buttonSelector);      // the clickable label/button
    var buttonLabel    = triggerButton.querySelector("span");           // inner <span> that shows current value
    var listWrapper    = container.querySelector(listSelector);         // the collapsible list element
    var listItems      = listWrapper.querySelectorAll(optionSelector);  // individual option <div>s

    // ── Calculate max visible height (if showElements > 0) ───────────────
    var maxVisibleHeight = 0;
    listItems.forEach(function (item, index) {
      if (index <= showElements - 1) {
        // Sum up the natural height of the first N items
        maxVisibleHeight += item.offsetHeight;
      }
    });

    // Collapse the list immediately (height 0); enable scroll only when capped
    gsap.set(listWrapper, {
      height:   0,
      overflow: maxVisibleHeight > 0 ? "auto" : "hidden"
    });

    // ── Close the dropdown ────────────────────────────────────────────────
    function closeDropdown() {
      gsap.to(listWrapper, {
        height:   0,
        duration: duration,
        ease:     ease,
        delay:    delay,
        onComplete: function () {
          container.classList.remove("is-active");
          if (onAnimComplete) onAnimComplete();
        }
      });
    }

    // ── Toggle open / close on button click ──────────────────────────────
    triggerButton.addEventListener("click", function () {
      if (container.classList.contains("is-active")) {
        // Already open → close it
        closeDropdown();
      } else {
        // Closed → animate open
        gsap.to(listWrapper, {
          height:   maxVisibleHeight > 0 ? maxVisibleHeight : "auto",
          duration: duration,
          ease:     ease,
          delay:    delay,
          onStart: function () {
            container.classList.add("is-active");
          },
          onComplete: function () {
            if (onAnimComplete) onAnimComplete();
          }
        });
      }
    });

    // ── Handle option selection ───────────────────────────────────────────
    listItems.forEach(function (item, index) {
      item.addEventListener("click", function (event) {
        var clickedTarget = event.target;

        // Update the visible button label with the selected item's inner HTML
        buttonLabel.innerHTML = clickedTarget
          .closest(optionSelector)
          .querySelector("span").innerHTML;

        // Fire the external callback with the selected item + container
        onOptionSelect(item, container);

        // Close the dropdown after selection
        closeDropdown();
      });
    });

    // ── Close when clicking outside the container ─────────────────────────
    document.addEventListener("click", function (event) {
      if (!container.contains(event.target)) {
        closeDropdown();
      }
    });
  }
};


/**
 * ============================================================
 * Custom Select Initialiser
 * ============================================================
 * Replaces native <select> elements with custom-styled dropdowns.
 * Skips country/province selects that have their own handling.
 * Builds the HTML structure, then hands off to xl() for animation.
 */
!(function () {
  // Grab the first native <select> on the page
  var firstSelect = document.querySelector("select");

  // Skip if: no select found, or it's a country/province select (handled elsewhere)
  if (
    !firstSelect ||
    firstSelect.classList.contains("js-country-select") ||
    firstSelect.classList.contains("js-province-select")
  ) {
    return;
  }

  // ── Run the custom-select factory ────────────────────────────────────────
  (function buildCustomSelects(config) {
    var targetElements; // NodeList / Array of <select> elements to replace

    if (!config) config = {};

    // ── Resolve which <select> elements to process ────────────────────────
    if (config.select) {
      if (typeof config.select === "string") {
        targetElements = document.querySelectorAll(config.select);
      } else if (config.select.length && !config.select.tagName) {
        targetElements = Array.from(config.select);
      } else if (
        typeof config.select === "object" &&
        "tagName" in config.select
      ) {
        targetElements = [config.select];
      }
    } else {
      targetElements = document.querySelectorAll("select");
    }

    if (!targetElements || targetElements.length === 0) return;

    // ── Destructure dropdown sub-config ───────────────────────────────────
    var dropdownProps   = config.dropdownProps    || {};
    var containersAttr  = dropdownProps.containers  || "[data-form-select]";
    var buttonAttr      = dropdownProps.buttonSelector || "[data-select-name]";
    var listAttr        = dropdownProps.listSelector   || "[data-select-list]";
    var optionAttr      = dropdownProps.optionSelector || "[data-select-item]";
    var classPrefix     = config.classPrefix       || "custom-select"; // CSS class prefix

    // ── Build a custom dropdown for each <select> ─────────────────────────
    targetElements.forEach(function (selectEl) {
      buildSingleCustomSelect(selectEl);
    });

    /**
     * Replaces a single native <select> with a custom HTML dropdown.
     * @param {HTMLSelectElement} selectEl
     */
    function buildSingleCustomSelect(selectEl) {
      var options       = selectEl.querySelectorAll("option");
      var optionsHTML   = ""; // accumulated HTML for all custom items

      // ── Build each custom option <div> ───────────────────────────────
      options.forEach(function (option) {
        var disabledClass = "";
        if (option.hasAttribute("disabled")) {
          disabledClass = " " + classPrefix + "__item--disabled";
        }

        // If the option text contains " (" (e.g. "Red (Limited)"),
        // wrap the parenthetical in a <span> for independent styling
        var optionText = option.innerHTML;
        if (option.innerHTML.includes(" (")) {
          var parts = option.innerHTML.split(" (");
          optionText = parts[0] + " <span>(" + parts[1] + "</span>";
        }

        // Build the custom item element; data attribute holds the option value
        var itemHTML =
          '<div class="' + classPrefix + "__item" + disabledClass + '" ' +
          optionAttr.slice(1, -1) + '="' + option.getAttribute("value") + '">\n' +
          "\t\t\t\t<span>" + optionText + "</span>\n" +
          "\t\t\t</div>";

        optionsHTML += itemHTML;
      });

      // ── Determine the initially-selected label ───────────────────────
      // Prefer an option with [selected] attribute; fall back to first option
      var initialText = selectEl.querySelector("option[selected]")
        ? selectEl.querySelector("option[selected]").innerHTML
        : options[0].innerHTML;

      // Debug logging (left from original source)
      console.log("selectedOptionText");
      console.log(initialText);
      console.log(initialText.includes(" ("));

      // Apply the same parenthetical-split treatment to the selected label
      var selectedOptionText = initialText;
      if (initialText.includes(" (")) {
        var parts = initialText.split(" (");
        selectedOptionText = parts[0] + " <span>(" + parts[1] + "</span>";
      }

      // ── Assemble the full custom dropdown HTML ───────────────────────
      var dropdownHTML =
        '<div class="' + classPrefix + '" ' + containersAttr.slice(1, -1) + ">\n" +
        '\t\t\t<div class="' + classPrefix + '__name" ' + buttonAttr.slice(1, -1) + ">\n" +
        "\t\t\t\t<span>" + selectedOptionText + "</span>\n" +
        "\t\t\t</div>\n" +
        '\t\t\t<div class="' + classPrefix + '__list" ' + listAttr.slice(1, -1) + ">\n" +
        "\t\t\t\t" + optionsHTML + "\n" +
        "\t\t\t</div>\n" +
        "\t\t</div>";

      // Hide the original <select> and inject the custom dropdown after it
      selectEl.classList.add("is-hidden");
      selectEl.insertAdjacentHTML("afterend", dropdownHTML);
    }

    // ── Wire up xl() for animation + sync back to native <select> ────────
    var externalOnOptionSelect = dropdownProps.onOptionSelect; // store any pre-existing callback

    // Build the xl() config, merging in a callback that keeps the native
    // <select> value in sync with the custom dropdown selection
    var xlConfig = {
      containers: containersAttr,
      onOptionSelect: function (selectedItem, dropdownContainer) {
        // The native <select> sits immediately before the custom dropdown in the DOM
        dropdownContainer.previousElementSibling.value =
          selectedItem.getAttribute("data-select-item");

        // Also call any externally-supplied callback
        if (externalOnOptionSelect) externalOnOptionSelect(selectedItem, dropdownContainer);
      }
    };

    // Merge the xl config back into dropdownProps and launch xl()
    Object.assign(dropdownProps, xlConfig);
    xl(dropdownProps);
  })(
    // ── Configuration passed into buildCustomSelects() ──────────────────
    {
      select:      "select",
      classPrefix: "custom-select",
      dropdownProps: {
        containers:     "[data-form-select]",
        buttonSelector: "[data-select-name]",
        listSelector:   "[data-select-list]",
        optionSelector: "[data-select-item]",
        showElements:   0,     // 0 = show all options without scroll
        duration:       0.3,   // animation duration in seconds
        ease:           "power1.out",
        delay:          0,

        /**
         * Called every time the user picks an option inside a product form.
         * Handles:
         *  - Variant availability checking
         *  - Preorder state toggling
         *  - Multi-option column synchronisation
         *  - Low-stock threshold messaging
         */
        onOptionSelect: function (selectedItem, dropdownContainer) {
          var selectedOption = null; // will hold the matched <option> element

          // Only run the product-form logic when inside a [data-product-form]
          var productForm = dropdownContainer.closest("form[data-product-form]");
          if (!productForm) return;

          var nativeSelect  = dropdownContainer.previousElementSibling; // the hidden <select>
          var allOptions    = nativeSelect.querySelectorAll("option");
          var matchedOption = Array.from(allOptions).find(function (opt) {
            return opt.value === selectedItem.dataset.selectItem;
          });

          // Mark the matched option as [selected] in the native <select>
          allOptions.forEach(function (opt) {
            opt.removeAttribute("selected");
            if (opt === matchedOption) opt.setAttribute("selected", "");
          });

          selectedOption = matchedOption;

          // If this is the primary (first) column selector, run column-change logic
          if (nativeSelect.classList.contains("js-first-select")) {
            selectedOption = window.changeFirstColumn(matchedOption, productForm);
          }

          var optionGroupCount = productForm.querySelectorAll("[data-option-group]").length;

          if (optionGroupCount > 1) {
            // Multi-option product: check availability then sync second column
            window.checkAvaliability(selectedOption, productForm);

            if (nativeSelect.classList.contains("js-first-select")) {
              // Automatically click the matching item in the second column
              var selectedValue     = selectedOption.value;
              var secondColumnDropdown = nativeSelect.nextSibling; // custom select sibling
              if (secondColumnDropdown) {
                secondColumnDropdown
                  .querySelector(".custom-select__item[data-select-item=" + selectedValue + "]")?.click();
              }
            }
          } else {
            // Single-option product: just check availability
            window.checkAvaliability(selectedOption, productForm);
          }

          // ── Low-stock threshold message ──────────────────────────────
          var thresholdInput = document.querySelector("[data-js-threshold-count]");
          if (thresholdInput) {
            var threshold       = parseInt(thresholdInput.value || 0);
            var variantQuantity = parseInt(selectedOption.dataset.quantity);
            var thresholdMsg    = document.querySelector("[data-product-threhold-message]");

            if (threshold < variantQuantity || variantQuantity === 0) {
              // Stock is fine (or unlimited) — hide the message
              thresholdMsg.classList.add("hidden");
            } else {
              // Stock is at or below threshold — show "Only N left!"
              thresholdMsg.textContent = "Only " + variantQuantity + " left!";
              thresholdMsg.classList.remove("hidden");
            }
          }
        }
      }
    }
  );

  // ── "is-choise" class for [data-select-block] containers ─────────────────
  // Marks a select block as having had a choice made once any item is clicked
  document.querySelectorAll("[data-select-block]").forEach(function (block) {
    block.querySelectorAll(".custom-select__item").forEach(function (item) {
      item.addEventListener("click", function () {
        block.classList.add("is-choise");
      });
    });
  });
})();


/**
 * ============================================================
 * Product Form — Add to Cart Logic
 * ============================================================
 * Finds every [data-product-form] on the page and wires up
 * submit handlers that call addToCart() with the correct
 * variant ID and any custom line-item properties.
 */

var productForms = document.querySelectorAll("[data-product-form]");

/**
 * Toggles the visibility of the "Add to Cart" vs "Sold Out" buttons.
 * @param {Element} form      The product form element.
 * @param {boolean} isSoldOut True → show sold-out state; false → show add-to-cart.
 */
function toggleSoldOutState(form, isSoldOut) {
  var addButton    = form.querySelector("[data-form-submit]");
  var soldOutLabel = form.querySelector("[data-form-sold]");
  var tnPdpButton  = form.querySelector("#tn-pdp-button"); // optional third-party button

  if (isSoldOut) {
    addButton.classList.add("is-hidden");
    soldOutLabel.classList.remove("is-hidden");
    if (tnPdpButton) tnPdpButton.classList.add("is-hidden");
  } else {
    addButton.classList.remove("is-hidden");
    soldOutLabel.classList.add("is-hidden");
    if (tnPdpButton) tnPdpButton.classList.remove("is-hidden");
  }
}

if (productForms.length > 0) {
  productForms.forEach(function (form) {
    // ── Build shared line-item properties ───────────────────────────────
    var properties = {};

    // Include preorder date string as a property if the product is on preorder
    var preorderValue = form.elements.preOrder.value;
    if (preorderValue !== "false") {
      properties["preorder"] = preorderValue;
    }

    // Include cross-sell discount flag if applicable
    var crossSellDiscount = form.elements.crossselldiscount.value;
    if (crossSellDiscount === "Yes") {
      properties["_crossselldiscount"] = "Yes";
    }

    // Include sale a property if the product final sale
    if(form.elements.SaleProperty){
      var saleValue = form.elements.SaleProperty.value;
        properties["Sale"] = saleValue;
    }

    // ── Route to the correct submit handler based on form type ──────────
    var formType = form.dataset.productForm;

    if (formType === "one-option") {
      // Single variant selector — reads value from a [data-option-select] <select>
      initOneOptionForm(form);
    } else if (formType === "multiply-options") {
      // Multiple variant columns — reads from the active column's selected option
      initMultiplyOptionsForm(form);
    } else {
      console.log("other-options");
    }

    /**
     * Wires up submit for a product with a single option selector.
     * @param {Element} form
     */
    function initOneOptionForm(form) {
      form.addEventListener("submit", function (event) {
        event.preventDefault();

        var variantId;
        var selectEl = form.querySelector("[data-option-select]");

        // Find the <option> that matches the current select value and grab its variant ID
        selectEl.querySelectorAll("option").forEach(function (opt) {
          if (opt.value === selectEl.value) {
            variantId = opt.dataset.variantId;
          }
        });

        addToCart(variantId, properties);
      });
    }

    /**
     * Wires up submit for a product with multiple option groups (e.g. size + colour).
     * The second [data-option-group] holds the active column of variant options.
     * @param {Element} form
     */
    function initMultiplyOptionsForm(form) {
      // The second option group contains the variant-level selects
      var secondOptionGroup = form.querySelectorAll("[data-option-group]")[1];

      form.addEventListener("submit", function (event) {
        event.preventDefault();

        // The active column is marked with .js-active-now
        var activeSelectOptions = secondOptionGroup
          .querySelector(".js-active-now")
          .querySelectorAll("option");

        var selectedOption = Array.from(activeSelectOptions).find(function (opt) {
          return opt.selected;
        });

        var variantId = selectedOption.dataset.variantId;
        addToCart(variantId, properties);
      });
    }
  });
}


/**
 * window.checkAvaliability
 * ─────────────────────────
 * Reads the form's [data-metadata] JSON (a map of variantId → preorderDate|false),
 * looks up the chosen option's variant, then shows/hides preorder UI or
 * sold-out UI as appropriate.
 *
 * @param {HTMLOptionElement} selectedOption  The currently selected <option>.
 * @param {HTMLFormElement}   form            The product form.
 */
window.checkAvaliability = function (selectedOption, form) {
  var metadata    = JSON.parse(form.dataset.metadata); // { variantId: preorderDate | false }
  var preorderDate = false;

  // Find the entry whose key matches the selected variant ID
  for (var i = 0, entries = Object.entries(metadata); i < entries.length; i++) {
    var key   = entries[i][0];
    var value = entries[i][1];

    if (parseInt(key) === parseInt(selectedOption.dataset.variantId)) {
      preorderDate = value; // will be a date string or false
      break;
    }
  }

  // ── Update preorder UI ─────────────────────────────────────────────────
  (function updatePreorderUI(form, isPreorder, dateString) {
    var addToCartBtn  = form.querySelector(".js-button-add-tocart");
    var preorderBtn   = form.querySelector(".js-button-preorder");
    var preorderDate  = form.querySelector(".js-date-preorder");

    if (isPreorder) {
      // Show preorder button + estimated date; hide normal ATC button
      addToCartBtn.classList.add("is-hidden");
      preorderBtn.classList.remove("is-hidden");
      preorderDate.classList.remove("is-hidden");
      preorderDate.querySelector("span").innerHTML = dateString;
      form.elements.preOrder.value = dateString; // keep the hidden input current
    } else {
      // Show normal ATC button; hide preorder elements
      addToCartBtn.classList.remove("is-hidden");
      preorderBtn.classList.add("is-hidden");
      preorderDate.classList.add("is-hidden");
      form.elements.preOrder.value = false;
    }
  })(form, !!preorderDate, preorderDate);

  // Toggle the sold-out state based on whether the option element is disabled
  toggleSoldOutState(form, selectedOption.hasAttribute("disabled"));
};


/**
 * window.changeFirstColumn
 * ─────────────────────────
 * Called when the user changes the primary option (e.g. "Size").
 * Shows only the matching second-column <select> (e.g. the colour
 * options for that size), hides the rest, and returns the first
 * valid (non-disabled) option to use for availability checks.
 *
 * @param {HTMLOptionElement} selectedOption  The option just selected in column 1.
 * @param {HTMLFormElement}   form            The product form.
 * @returns {HTMLOptionElement} The option to use for availability checks.
 */
window.changeFirstColumn = function (selectedOption, form) {
  var returnOption    = selectedOption;
  var secondGroup     = form.querySelectorAll("[data-option-group]")[1]; // 2nd column container
  var allSecondSelects = secondGroup.querySelectorAll("select");

  // Find the <select> in column 2 whose [data-option-select] matches the
  // chosen option's [data-index] value
  var matchingSecondSelect = Array.from(allSecondSelects).find(function (sel) {
    return sel.dataset.optionSelect === selectedOption.dataset.index;
  });

  // Mark only the matching select as active; hide all others
  allSecondSelects.forEach(function (sel) {
    if (sel !== matchingSecondSelect) {
      sel.classList.remove("js-active-now");
    } else {
      sel.classList.add("js-active-now");
    }
  });

  // Show only the corresponding custom-select dropdown; hide the rest
  var matchingCustomSelect = matchingSecondSelect.nextElementSibling;
  secondGroup.querySelectorAll(".custom-select").forEach(function (customSel) {
    customSel.style.display = customSel === matchingCustomSelect ? "block" : "none";
  });

  // ── If the changed select is in the first option group, resolve the
  //    first non-disabled option to pass along for availability checks ───
  var allOptionGroups  = Array.prototype.slice.call(form.querySelectorAll("[data-option-group]"));
  var firstColumnGroup = selectedOption.closest("[data-option-group]");

  if (allOptionGroups.indexOf(firstColumnGroup) === 0) {
    var indexValue   = selectedOption.dataset.index;
    var firstEnabled = document.querySelector(
      '[data-option-select="' + indexValue + '"] option:not(:disabled)'
    );
    if (firstEnabled !== null) {
      returnOption = firstEnabled;
    }
  }

  // If the active second column has no enabled options, mark the form as sold out
  var activeSecondSelect = secondGroup.querySelector(".js-active-now");
  var firstActiveOption  = activeSecondSelect.querySelectorAll("option")[0];

  if (firstActiveOption.hasAttribute("disabled")) {
    toggleSoldOutState(form, true);
  } else {
    toggleSoldOutState(form, false);
  }

  return returnOption;
};


/**
 * addToCart
 * ─────────
 * Sends a POST request to Shopify's cart/add.js endpoint to add
 * a variant to the cart, then triggers cart-drawer and cart-icon
 * re-renders via section rendering.
 *
 * @param {string|number} variantId  The Shopify variant ID to add.
 * @param {Object}        properties Line-item properties (e.g. preorder date).
 */
function addToCart(variantId, properties) {
  var formData = {
    items: [
      {
        id:         variantId,
        quantity:   1,
        properties: properties
      }
    ],
    // Request Shopify to return updated section HTML for cart UI re-renders
    sections: "cart-drawer,cart-icon-bubble"
  };

  fetch(window.Shopify.routes.root + "cart/add.js", {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(formData)
  })
    .then(function (response) {
      return response.json();
    })
    .then(function (response) {
      // Re-render the cart drawer with the updated section HTML
      var cartDrawer = document.querySelector("cart-drawer");
      cartDrawer.renderContents(response);

      // Optional post-add hooks (defined elsewhere on the page)
      makeItASet(response.items[0]);          // "make it a set" upsell logic
      cartOffer(cartOfferDigitalProps);       // cart offer / digital upsell logic
    })
    .catch(function (error) {
      console.error("Error:", error);
    });
}


/**
 * eu (destructureTwo)
 * ────────────────────
 * Utility that safely destructures exactly two elements from an
 * iterable (Array, iterator, or array-like object).
 * Equivalent to a two-element destructuring assignment with a
 * fallback for non-standard iterables.
 *
 * @param  {*}      e  The value to destructure.
 * @param  {number} i  How many elements to extract (always 2 when called above).
 * @returns {Array}    An array of the first `i` elements.
 */
function eu(e, i) {
  // Fast path: plain Array
  if (Array.isArray(e)) return e;

  // Iterator protocol path
  var iterator = e == null ? null : (typeof Symbol !== "undefined" && e[Symbol.iterator]) || e["@@iterator"];

  if (iterator != null) {
    var step, done, collected = [], isDone = true, hasError = false, error;

    try {
      for (
        iterator = iterator.call(e);
        !(isDone = (step = iterator.next()).done) && (collected.push(step.value), !i || collected.length !== i);
        isDone = true
      ) { /* collecting */ }
    } catch (err) {
      hasError = true;
      error    = err;
    } finally {
      try {
        if (!isDone && iterator.return != null) iterator.return();
      } finally {
        if (hasError) throw error;
      }
    }

    return collected;
  }

  // Array-like fallback (handled by the outer `t` helper — see original minified code)
  // If we reach here without returning, throw a clear error
  throw new TypeError(
    "Invalid attempt to destructure non-iterable instance.\n" +
    "In order to be iterable, non-array objects must have a [Symbol.iterator]() method."
  );
}



	function makeItASet(item){
		//const cartRes = await fetch(`${window.location.origin}/cart.js`);
		//const cart = await cartRes.json();
		//if(cart.items.some(item => item.product_id == currentProductId) && !cart.items.some(item => item.product_id == miasProductId)){
		if(item.product_id == currentProductId){
			// if cart contains the current product but not the "Make it a set" product
			let itemContainerClass = 'drawer__cart-items-wrapper'// 'rebuy-cart__flyout-content'
			if(document.getElementsByClassName(itemContainerClass)[0]?.getElementsByClassName('make-it-a-set-msg').length < 1 ) {
				// if make-it-a-set-msg isn't already on the cart
				const target = document.getElementsByClassName(itemContainerClass)[0];
				const html = document.getElementById('make-it-a-set-msg-html').innerHTML;
				document.getElementById('make-it-a-set-msg-html').innerHTML = "";
				target.insertAdjacentHTML('afterbegin', html);

				document.getElementById('make-it-a-set-msg__close').addEventListener('click',e => {
					e.preventDefault();
					document.getElementById('make-it-a-set-msg').style.display = "none";
				})
				document.getElementById('make-it-a-set-msg__button').addEventListener('click', e => {
					e.preventDefault();
					let plLength = document.getElementsByClassName('js-product-layer').length;
					let productLayer = document.getElementsByClassName('js-product-layer')[plLength - 1];
					productLayer.classList.add('product-layer--open');
					productLayer.classList.add('product-layer--make-it-a-set');
					productLayer.style.zIndex = "2147483647";
					productLayer.getElementsByClassName('product-size__btn')[0].addEventListener('click', () => {
						productLayer.classList.remove('product-layer--open');
						document.getElementById('make-it-a-set-msg').style.display = "none";
						gtag("event", "make_it_a_set_add_to_cart", {
							added_product_handle: miasProductHandle,
							page_location: window.location.href
						});
					})
				})
			}
		}
	}


	// update inventory on ATC button when the variant radio button is clicked and inventory is below 11
	updateInventoryOnATC();

	let variantRadios = document.querySelector('variant-radios');

	variantRadios.querySelectorAll('.product-form__input--radios').forEach(radioGroup => {
		radioGroup.querySelectorAll('input').forEach( input => {
			console.log(input)
			input.addEventListener('change', e => {
				updateInventoryOnATC();
			});
		})
	})


	function updateInventoryOnATC(){
		let atcText = document.getElementsByClassName('product-size__btn')[0].getElementsByClassName('js-button-add-tocart')[0];
		let inventoryQuantityArr = JSON.parse(atcText.parentNode.parentNode.parentNode.getElementsByClassName('variant-inventory-data')[0].dataset.variantInventory);
		let formEl = atcText.parentNode.parentNode.parentNode;
		let variantId =	getActiveVariantId(formEl);
		let inventory = 0;
		if(variantId) {
			let variantArr = JSON.parse(formEl.parentNode.getElementsByTagName('script')[0].innerText)
			let index = variantArr.findIndex(v => v.id == variantId)
			//let inventory = inventoryQuantityArr[i];
			inventory = inventoryQuantityArr[index];

		}

		[...atcText.getElementsByClassName('inventory')].forEach(el => { el.remove()});
		if(inventory < 11){
			let span = document.createElement('span');
			span.classList.add('inventory');
			span.innerText = `${inventory} left -`;
			atcText.insertAdjacentElement('afterbegin', span);
		}	

	}

  function getActiveVariantId(formEl) {
    let secondOptionGroup = formEl.querySelectorAll("[data-option-group]")[1];
    //console.log('secondOptionGroup',secondOptionGroup)

    let active = secondOptionGroup.querySelector('.js-active-now')
    //console.log('active', active)
    let selectedOption = [...active.querySelectorAll('option')].find(optionEl => optionEl.selected);
    //console.log('selectedOption', selectedOption)

    if(!selectedOption) {
      return false
    } else {
      return selectedOption.dataset.variantId
    }
  }


})()





